import {
  CalendarDays,
  ChevronRight,
  MapPin,
  Search,
  Ticket,
  Users,
  X,
} from "@/components/ui/icons";
import { Image as ExpoImage } from "expo-image";
import { router } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { ShopLogo } from "@/components/brand/ShopLogo";
import { FloorPlanCanvas } from "@/components/floor-plan/FloorPlanCanvas";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Sheet } from "@/components/ui/Sheet";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { APP_BASE_URL } from "@/config";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { PublicShow, PublicShowPlan } from "@/hooks/usePublicShow";
import { type PublicShowInventory, usePublicShowInventory } from "@/hooks/usePublicShowInventory";
import { DESKTOP_BREAKPOINT } from "@/hooks/useResponsive";
import { formatCardIdentity } from "@/lib/catalog/cardIdentity";
import { preloadVendorApp } from "@/lib/platform/vendorChunk";
import { buylistPath } from "@/lib/shop/publicUrls";
import { type FloorPlanDocument, parseFloorPlan } from "@/lib/shows/floorPlan";
import { buildPlanPrintHtml, type PlanTableAssignment } from "@/lib/shows/planPublishing";
import { showCalendarUrls, showMapUrl } from "@/lib/shows/showActions";
import {
  availabilityLabel,
  type ShowCatalogCard,
  treatmentLabel,
} from "@/lib/shows/showCardFinding";
import { formatShowMoney } from "@/lib/shows/showOperations";
import { describeShowLocation, shouldCollapseShowMap } from "@/lib/shows/showWayfinding";
import { tableOptionDaysLabel } from "@/lib/shows/tableApplication";
import { useThemeColors } from "@/theme/theme";

/**
 * What the public route and the Organizer preview (TRO-1033) both render, so they can never
 * become two descriptions of the same Show. `state` widens past the public route's own
 * `PublicShow` (which can only ever be "published" or "canceled", since the backend 404s a Draft
 * to an anonymous caller) to also carry "draft", which only the preview route can see.
 */
export type ShowListingData = Omit<PublicShow, "state"> & {
  state: PublicShow["state"] | "draft";
};

/** A locally-saved pull sheet, keyed by the Vendor it belongs to. The Vendor's name and table
 * number aren't known until joined against `show.roster` and the parsed plan, which only
 * `ShowListingBody` has, so the caller passes the raw row and lets this component do the join. */
export type ShowListingPullSheet = {
  pullSheetId: string;
  participationId: string;
  itemCount: number;
};

const tableOrder = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function sessionLabel(
  session: { starts_at: string; ends_at: string; },
  timeZone: string,
): string {
  const format = (zone: string) => {
    const date = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const time = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour: "numeric",
      minute: "2-digit",
    });
    const starts = new Date(session.starts_at);
    const ends = new Date(session.ends_at);
    const startDate = date.format(starts);
    const endDate = date.format(ends);
    return startDate === endDate
      ? `${startDate} · ${time.format(starts)}–${time.format(ends)}`
      : `${startDate} ${time.format(starts)}–${endDate} ${time.format(ends)}`;
  };
  try {
    return format(timeZone || "UTC");
  } catch {
    return format("UTC");
  }
}

function gameLabel(game: string): string {
  if (game.toLocaleLowerCase() === "pokemon") return "Pokémon";
  return game.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toLocaleUpperCase());
}

function readablePlan(
  plan: PublicShowPlan | null,
): (PublicShowPlan & { document: FloorPlanDocument; }) | null {
  if (!plan) return null;
  try {
    return {
      ...plan,
      document: typeof plan.document === "string"
        ? parseFloorPlan(plan.document)
        : parseFloorPlan(JSON.stringify(plan.document)),
    };
  } catch {
    return null;
  }
}

function CardResultRow({ card, onPress }: {
  card: ShowCatalogCard;
  onPress: () => void;
}) {
  const c = useThemeColors();
  const identity = formatCardIdentity(
    card.set_name,
    card.collector_number,
    treatmentLabel(card),
  );
  return (
    <Pressable
      testID="show-card-row"
      accessibilityRole="button"
      accessibilityLabel={`${card.name}, ${availabilityLabel(card.vendor_count, card.quantity)}`}
      onPress={onPress}
      className="min-h-20 flex-row items-center gap-3 rounded-xl border border-border bg-surface p-3 active:bg-surface-muted"
    >
      {card.image_url
        ? (
          <Image
            source={{ uri: card.image_url }}
            className="h-14 w-10 rounded-md bg-surface-muted"
            resizeMode="cover"
            accessible={false}
            accessibilityIgnoresInvertColors
          />
        )
        : (
          <View className="h-14 w-10 items-center justify-center rounded-md bg-surface-muted">
            <Text className="text-[10px] font-label uppercase text-muted-foreground">
              {(card.game ?? "TCG").slice(0, 3)}
            </Text>
          </View>
        )}
      <View className="min-w-0 flex-1 gap-1">
        <Text className="text-[15px] font-title text-foreground" numberOfLines={2}>
          {card.name}
        </Text>
        {identity
          ? (
            <Text className="text-xs font-body text-muted-foreground" numberOfLines={1}>
              {identity}
            </Text>
          )
          : null}
        <Badge
          label={availabilityLabel(card.vendor_count, card.quantity)}
          className="bg-accent-soft"
          // Same contrast fix as the hero status badge above (TRO-1375).
          textClassName="text-foreground"
        />
      </View>
      <ChevronRight size={18} color={c.subtle} />
    </Pressable>
  );
}

export function ShowListingBody({
  show,
  initialQuery = "",
  onSearchResult,
  hunt,
  pullSheets,
  onOpenPullSheet,
}: {
  show: ShowListingData;
  initialQuery?: string;
  /** Fired once settled results are in, so the caller can sync the URL and log analytics without
   * this component knowing about either. */
  onSearchResult?: (
    info: {
      query: string;
      cards: ShowCatalogCard[];
      zeroResultCause: NonNullable<PublicShowInventory["zero_result_cause"]> | null;
    },
  ) => void;
  hunt?: { count: number; onOpen: () => void; };
  pullSheets?: ShowListingPullSheet[];
  onOpenPullSheet?: (participationId: string, pullSheetId: string) => void;
}) {
  const colors = useThemeColors();
  const { width, fontScale } = useWindowDimensions();
  const isDesktop = Platform.OS === "web" && width >= DESKTOP_BREAKPOINT;
  const [query, setQuery] = useState(initialQuery);
  const settledQuery = useDebouncedValue(query, 250);
  const inventory = usePublicShowInventory(show.public_id, settledQuery);
  const [rosterQuery, setRosterQuery] = useState("");
  const [selectedParticipationId, setSelectedParticipationId] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const q = settledQuery.trim();
    // `usePublicShowInventory` disables its query rather than clearing `data` once the box is
    // cleared, so `inventory.data` can still hold the last non-empty search's results; `!q` is
    // what actually stops this from reporting a stale result as fresh. The caller (not this
    // component) owns deduplication, because it also has to wait on its own analytics client
    // becoming ready, which can resolve after this effect already has its result in hand.
    if (!onSearchResult || !q || !inventory.data || inventory.isPlaceholderData) return;
    const cards = inventory.data.cards;
    const zeroResultCause = inventory.data.zero_result_cause ?? null;
    // A confirmed-empty result (the backend found nothing worth naming a cause for) has nothing
    // for analytics to record; `showSearched` itself throws on a zero count with no cause, since a
    // silently-swallowed one there would look like a working search that just never fires.
    if (cards.length === 0 && !zeroResultCause) return;
    onSearchResult({ query: q, cards, zeroResultCause });
  }, [inventory.data, inventory.isPlaceholderData, onSearchResult, settledQuery]);

  const plan = useMemo(() => readablePlan(show.plan), [show.plan]);
  const assignments = useMemo<PlanTableAssignment[]>(() =>
    plan?.assignments.map((assignment) => ({
      tableId: assignment.table_id,
      participationId: assignment.participation_id,
      vendorName: assignment.vendor_name,
      searchable: assignment.searchable,
    })) ?? [], [plan?.assignments]);
  const selected = show.roster.find((vendor) =>
    vendor.participation_id === selectedParticipationId
  );
  const roster = useMemo(() => {
    const term = rosterQuery.trim().toLocaleLowerCase();
    return term
      ? show.roster.filter((vendor) => vendor.name.toLocaleLowerCase().includes(term))
      : show.roster;
  }, [rosterQuery, show.roster]);
  const tableNumbers = useMemo(
    () => new Map(plan?.document.tables.map((table) => [table.id, table.number]) ?? []),
    [plan],
  );
  const yourLists = useMemo(() =>
    (pullSheets ?? []).flatMap((sheet) => {
      const vendor = show.roster.find((row) => row.participation_id === sheet.participationId);
      if (!vendor) return [];
      const numbers = vendor.table_ids
        .map((tableId) => tableNumbers.get(tableId) ?? tableId)
        .sort((a, b) => tableOrder.compare(a, b));
      return [{ ...sheet, vendorName: vendor.name, tableNumbers: numbers }];
    }).sort((a, b) =>
      tableOrder.compare(a.tableNumbers[0] ?? "", b.tableNumbers[0] ?? "")
      || a.vendorName.localeCompare(b.vendorName)
    ), [pullSheets, show.roster, tableNumbers]);
  const tableStates = useMemo(() => {
    if (!plan) return {};
    const byTable = new Map(assignments.map((assignment) => [assignment.tableId, assignment]));
    const states: Record<string, "unassigned" | "assigned" | "searchable"> = {};
    for (const table of plan.document.tables) {
      const assignment = byTable.get(table.id);
      states[table.id] = !assignment
        ? "unassigned"
        : assignment.searchable
        ? "searchable"
        : "assigned";
    }
    return states;
  }, [assignments, plan]);
  const mapCollapsed = !!plan && shouldCollapseShowMap(
    plan.document,
    show.roster.map((vendor) => ({ tableIds: vendor.table_ids })),
    fontScale,
  );

  const print = () => {
    if (!plan || Platform.OS !== "web") return;
    const popup = window.open("", "_blank");
    if (!popup) return;
    popup.opener = null;
    popup.document.write(buildPlanPrintHtml({
      plan: plan.document,
      title: show.name,
      revision: plan.revision,
      publishedAt: new Date(plan.published_at).toLocaleString(),
      variant: "public",
      assignments,
    }));
    popup.document.close();
    popup.addEventListener("load", () => popup.print(), { once: true });
  };

  const calendarUrls = showCalendarUrls({
    publicId: show.public_id,
    name: show.name,
    description: show.description,
    venueName: show.venue_name,
    venueAddress: show.venue_address,
    sessions: (show.sessions ?? []).map((session) => ({
      startsAt: session.starts_at,
      endsAt: session.ends_at,
    })),
  });
  const openAction = async (url: string, failure: string, closeCalendar = false) => {
    setActionError(null);
    try {
      await Linking.openURL(url);
      if (closeCalendar) setCalendarOpen(false);
    } catch {
      setActionError(failure);
    }
  };

  const searchPanel = (
    <View
      testID="show-search"
      className={isDesktop
        ? "mt-10 w-full flex-row items-center gap-8 rounded-xl border border-border bg-surface p-7"
        : "mt-6 w-full gap-4 rounded-xl border border-primary bg-accent-soft p-5"}
    >
      <View className={isDesktop ? "w-80 shrink-0 gap-1" : "gap-1"}>
        <Text
          accessibilityRole="header"
          aria-level={2}
          className="text-2xl font-title text-foreground"
        >
          Find a card at this Show
        </Text>
        <Text className="font-body text-sm text-muted-foreground">
          Search cards listed by {show.roster.length || "the"}{" "}
          {show.roster.length === 1 ? "Vendor" : "Vendors"}, then see which table to visit.
        </Text>
        {hunt
          ? (
            <Button
              title={hunt.count ? "Open my Hunt List" : "Build a Hunt List"}
              size="sm"
              className="mt-3 self-start"
              onPress={hunt.onOpen}
            />
          )
          : null}
      </View>
      <View className={isDesktop ? "relative min-w-0 flex-1" : "relative"}>
        <View className="absolute left-3 z-10 h-11 justify-center" aria-hidden>
          <Search size={18} color={colors.subtle} />
        </View>
        <Input
          testID="show-search-input"
          value={query}
          onChangeText={setQuery}
          placeholder="Search by card name or set"
          accessibilityLabel="Search cards at this Show"
          autoCorrect={false}
          maxLength={120}
          returnKeyType="search"
          className={query ? "pl-10 pr-11" : "pl-10"}
        />
        {query
          ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear card search"
              onPress={() => setQuery("")}
              className="absolute right-0 h-11 w-11 items-center justify-center"
            >
              <X size={17} color={colors.subtle} />
            </Pressable>
          )
          : null}
      </View>
    </View>
  );

  const tableOptions = show.table_options ?? [];
  const showSessions = show.sessions ?? [];
  const vendorTables = show.state !== "canceled" && show.phase !== "completed"
      && (show.applications_open || show.public_table_details)
    ? (
      <View testID="show-vendor-tables" className="mt-10 gap-2 border-t border-border pt-8">
        <Text
          accessibilityRole="header"
          aria-level={2}
          className="text-2xl font-title text-foreground"
        >
          Vendor tables
        </Text>
        {!show.applications_open || !tableOptions.length
          ? (
            <Text className="max-w-[720px] font-body text-base leading-6 text-muted-foreground">
              {show.applications_open
                ? "This Organizer is accepting Vendor applications for this Show."
                : "Tables at this Show are offered by invitation from the Organizer."}
            </Text>
          )
          : null}
        {tableOptions.length
          ? (
            <View
              className={isDesktop ? "mt-2 flex-row flex-wrap gap-3" : "mt-2 max-w-[720px] gap-2"}
            >
              {tableOptions.map((option) => {
                const days = tableOptionDaysLabel(option, showSessions, show.timezone ?? "UTC");
                return (
                  <Card
                    key={option.public_id}
                    testID="show-table-option"
                    className={isDesktop ? "basis-[32%]" : ""}
                  >
                    <View className="flex-row flex-wrap items-baseline justify-between gap-x-3">
                      <Text className="font-title text-foreground">{option.name}</Text>
                      <Text className="font-title text-foreground">
                        {formatShowMoney(option.unit_fee, option.currency)}
                      </Text>
                    </View>
                    <Text className="mt-1 font-body text-sm leading-5 text-muted-foreground">
                      {option.configuration}
                    </Text>
                    {days
                      ? (
                        <Text className="mt-0.5 text-xs font-body text-muted-foreground">
                          {days}
                        </Text>
                      )
                      : null}
                    {option.available
                      ? (
                        <Badge
                          label="Available"
                          className="mt-2 bg-gain/10"
                          textClassName="text-gain"
                          testID="show-table-option-available"
                        />
                      )
                      : (
                        <Badge
                          label="Not available"
                          className="mt-2 bg-surface-muted"
                          textClassName="text-muted-foreground"
                          testID="show-table-option-unavailable"
                        />
                      )}
                  </Card>
                );
              })}
            </View>
          )
          : null}
        {show.public_table_details
          ? (
            <Text
              testID="show-table-details"
              className="mt-2 max-w-[720px] font-body text-base leading-7 text-foreground"
            >
              {show.public_table_details}
            </Text>
          )
          : null}
        {
          /* This page renders for both an anonymous visitor and an authenticated Organizer preview;
          applying still goes through the public sign-in gate either way, so the button behaves
          identically in both modes. */
        }
        {show.applications_open
          ? (
            <View className="mt-4 max-w-[720px] flex-row">
              <Button
                testID="show-apply-for-table"
                title="Apply for a table"
                onPress={() =>
                  void preloadVendorApp().then(() => router.push(`/apply/${show.public_id}`))}
              />
            </View>
          )
          : null}
      </View>
    )
    : null;

  return (
    <View className="w-full self-center" style={{ maxWidth: 1180 }}>
      <View testID="show-hero" className={isDesktop ? "mt-10" : "mt-6"}>
        <View
          className={isDesktop
            ? "flex-row items-center gap-12"
            : "flex-col overflow-hidden rounded-xl border border-border bg-surface"}
        >
          <View
            className={isDesktop
              ? "min-w-0 flex-1 justify-center gap-0 py-10 pl-2"
              : "min-w-0 flex-1 gap-0 p-5"}
          >
            <View className="flex-row flex-wrap items-center gap-2">
              <View testID="show-status">
                <Badge
                  label={show.state === "canceled"
                    ? "Canceled"
                    : show.state === "draft"
                    ? "Draft"
                    : show.phase === "live"
                    ? "Live now"
                    : show.phase[0].toUpperCase() + show.phase.slice(1)}
                  className={show.state === "canceled"
                    ? "bg-warning-soft"
                    : show.state === "draft"
                    ? "bg-surface-muted"
                    : "bg-accent-soft"}
                  // `text-primary` on `bg-accent-soft` at this badge's `text-xs` measures 4.43:1,
                  // under the 4.5:1 axe asks (TRO-1375's fix for the same pairing); `text-foreground`
                  // clears AA.
                  textClassName={show.state === "canceled"
                    ? "text-warning"
                    : show.state === "draft"
                    ? "text-muted-foreground"
                    : "text-foreground"}
                />
              </View>
              {show.games?.map((game) => (
                <Badge
                  key={game}
                  label={gameLabel(game)}
                  className="bg-surface-muted"
                  textClassName="text-muted-foreground"
                />
              ))}
            </View>

            <Text
              accessibilityRole="header"
              aria-level={1}
              className={isDesktop
                ? "mt-4 text-5xl font-title tracking-tight text-foreground"
                : "mt-4 text-3xl font-title tracking-tight text-foreground"}
            >
              {show.name}
            </Text>

            <View className={isDesktop ? "mt-8 gap-5" : "mt-6 gap-4"}>
              <View className="flex-row items-start gap-3">
                <View
                  className={isDesktop
                    ? "h-8 w-8 shrink-0 items-center justify-center"
                    : "h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft"}
                >
                  <MapPin size={19} color={colors.primary} />
                </View>
                <View className="min-w-0 flex-1">
                  <Text className="font-title text-foreground">{show.venue_name}</Text>
                  <Text className="mt-0.5 font-body text-sm leading-5 text-muted-foreground">
                    {show.venue_address}
                  </Text>
                </View>
              </View>

              {show.sessions?.length
                ? (
                  <View testID="show-schedule" className="flex-row items-start gap-3">
                    <View
                      className={isDesktop
                        ? "h-8 w-8 shrink-0 items-center justify-center"
                        : "h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft"}
                    >
                      <CalendarDays size={19} color={colors.primary} />
                    </View>
                    <View className="min-w-0 flex-1 gap-1">
                      {show.sessions.map((session) => (
                        <Text key={session.public_id} className="font-title text-foreground">
                          {sessionLabel(session, show.timezone ?? "UTC")}
                        </Text>
                      ))}
                      <Text className="text-xs font-body text-muted-foreground">
                        Local venue time
                      </Text>
                    </View>
                  </View>
                )
                : null}

              {show.admission
                ? (
                  <View className="flex-row items-start gap-3">
                    <View
                      className={isDesktop
                        ? "h-8 w-8 shrink-0 items-center justify-center"
                        : "h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft"}
                    >
                      <Ticket size={19} color={colors.primary} />
                    </View>
                    <View className="min-w-0 flex-1">
                      <Text className="text-xs font-label uppercase tracking-wide text-muted-foreground">
                        Admission
                      </Text>
                      <Text className="mt-0.5 font-title text-foreground">{show.admission}</Text>
                    </View>
                  </View>
                )
                : null}
            </View>

            {isDesktop ? null : searchPanel}

            <View className="mt-6 flex-row flex-wrap items-center gap-3 border-t border-border pt-5">
              {show.roster.length
                ? (
                  <View className="flex-row items-center gap-2">
                    <Users size={17} color={colors.subtle} />
                    <Text className="font-body text-sm text-muted-foreground">
                      {show.roster.length} {show.roster.length === 1 ? "Vendor" : "Vendors"} listed
                    </Text>
                  </View>
                )
                : null}
              {show.plan_state === "populated"
                ? (
                  <Text className="font-body text-sm text-muted-foreground">
                    Venue map available below
                  </Text>
                )
                : null}
              {show.applications_open
                ? (
                  <Text
                    testID="show-applications-signpost"
                    className="font-body text-sm text-muted-foreground"
                  >
                    Vendor applications open
                  </Text>
                )
                : null}
            </View>

            <View className="mt-5 flex-row flex-wrap items-center gap-2">
              {show.state !== "canceled"
                ? (
                  <>
                    {show.external_action_url && show.external_action_label
                      ? (
                        <Button
                          title={show.external_action_label}
                          size="lg"
                          accessibilityRole="link"
                          onPress={() =>
                            void openAction(
                              show.external_action_url!,
                              "The Organizer link could not be opened.",
                            )}
                        />
                      )
                      : null}
                    {calendarUrls
                      ? (
                        <Button
                          title="Add to calendar"
                          size="lg"
                          variant="outline"
                          onPress={() => {
                            setActionError(null);
                            setCalendarOpen(true);
                          }}
                        />
                      )
                      : null}
                    <Button
                      title="Open in Maps"
                      size="lg"
                      variant="outline"
                      accessibilityRole="link"
                      onPress={() =>
                        void openAction(
                          showMapUrl(
                            { venueName: show.venue_name, venueAddress: show.venue_address },
                            Platform.OS === "ios",
                          ),
                          "The map could not be opened.",
                        )}
                    />
                  </>
                )
                : null}
              <View className="ml-auto">
                <ThemeToggle />
              </View>
            </View>
            {actionError
              ? (
                <Text accessibilityRole="alert" className="mt-2 font-body text-sm text-loss">
                  {actionError}
                </Text>
              )
              : null}

            {show.organizer
              ? (
                <View
                  testID="show-organizer"
                  className="mt-6 flex-row items-center gap-3 border-t border-border pt-5"
                >
                  <ShopLogo
                    name={show.organizer.display_name}
                    uri={show.organizer.logo_url}
                    size={isDesktop ? 52 : 44}
                  />
                  <Text className="min-w-0 flex-1 font-body text-sm text-muted-foreground">
                    Organized by{" "}
                    <Text className="font-title text-foreground">
                      {show.organizer.display_name}
                    </Text>
                  </Text>
                </View>
              )
              : null}
          </View>
          {show.poster_url
            ? (
              <View
                className={isDesktop
                  ? "w-full overflow-hidden rounded-xl border border-border bg-surface-muted"
                  : "w-full bg-surface-muted"}
                style={isDesktop
                  ? { width: "45%", maxWidth: 520, aspectRatio: 4 / 5 }
                  : { aspectRatio: 16 / 10 }}
              >
                <ExpoImage
                  source={{ uri: show.poster_url }}
                  style={{ width: "100%", height: "100%" }}
                  contentFit="contain"
                  cachePolicy="memory-disk"
                  accessible
                  accessibilityLabel={`${show.name} poster`}
                />
              </View>
            )
            : null}
        </View>
      </View>

      {isDesktop ? searchPanel : null}

      {yourLists?.length
        ? (
          <View
            testID="show-your-lists"
            className="mt-5 w-full max-w-[860px] self-center gap-2 border-t border-border pt-4"
          >
            <Text
              accessibilityRole="header"
              aria-level={2}
              className="text-lg font-title text-foreground"
            >
              Your lists
            </Text>
            {yourLists.map((list) => {
              const tableLabel = list.tableNumbers.length === 1
                ? `Table ${list.tableNumbers[0]}`
                : list.tableNumbers.length > 1
                ? `Tables ${list.tableNumbers.join(", ")}`
                : "Table not assigned";
              const countLabel = `${list.itemCount} ${list.itemCount === 1 ? "item" : "items"}`;
              return (
                <Pressable
                  key={list.pullSheetId}
                  testID="show-your-list-row"
                  accessibilityRole="link"
                  accessibilityLabel={`${list.vendorName}, ${countLabel}, ${tableLabel}`}
                  onPress={() => onOpenPullSheet?.(list.participationId, list.pullSheetId)}
                  className="min-h-12 flex-row items-center justify-between rounded-lg border border-border bg-surface p-3 active:bg-surface-muted"
                >
                  <View className="min-w-0 flex-1">
                    <Text className="font-title text-foreground">{list.vendorName}</Text>
                    <Text className="mt-0.5 text-xs font-body text-muted-foreground">
                      {countLabel} · {tableLabel}
                    </Text>
                  </View>
                  <ChevronRight aria-hidden size={18} color={colors.subtle} />
                </Pressable>
              );
            })}
          </View>
        )
        : null}

      {query.trim()
        ? (
          <View
            testID="show-search-results"
            className="mt-4 w-full max-w-[860px] self-center gap-2"
            accessibilityLiveRegion="polite"
          >
            {show.phase === "upcoming" && inventory.data
              ? (
                <Text className="font-body text-xs leading-4 text-warning">
                  Inventory may change before doors open. Vendors keep selling from this stock until
                  then.
                </Text>
              )
              : null}
            {inventory.data && (query.trim() !== settledQuery.trim() || inventory.isFetching)
              ? (
                <Text
                  accessibilityLiveRegion="polite"
                  className="text-xs font-body text-muted-foreground"
                >
                  Updating results…
                </Text>
              )
              : null}
            {(query.trim() !== settledQuery.trim() || inventory.isLoading) && !inventory.data
              ? (
                <View className="min-h-20 items-center justify-center rounded-xl border border-border bg-surface p-4">
                  <ActivityIndicator />
                  <Text className="mt-2 font-body text-muted-foreground">Searching the Show…</Text>
                </View>
              )
              : inventory.error
              ? (
                <View
                  testID="show-search-unavailable"
                  accessibilityRole="alert"
                  className="rounded-xl border border-border bg-surface p-4"
                >
                  <Text className="font-title text-foreground">Card search is unavailable</Text>
                  <Text className="mt-1 font-body text-muted-foreground">
                    The Show details, floor plan, and Vendor roster are still available below.
                  </Text>
                  <View className="mt-3 self-start">
                    <Button
                      title="Try search again"
                      variant="outline"
                      onPress={() => void inventory.refetch()}
                    />
                  </View>
                </View>
              )
              : inventory.data?.cards.length === 0
              ? (
                <View className="rounded-xl border border-border bg-surface p-4">
                  <Text className="font-title text-foreground">
                    {inventory.data.zero_result_cause === "not_listed"
                      ? "No Vendor has that card listed"
                      : inventory.data.zero_result_cause === "no_catalog_match"
                      ? "No catalog card matches that search"
                      : "No cards match at this Show"}
                  </Text>
                  <Text className="mt-1 font-body text-muted-foreground">
                    {inventory.data.zero_result_cause === "not_listed"
                      ? "The card matched the catalog, but no Vendor has it in a Published Location for this Show."
                      : "Try the card name on its own. Set and number can narrow it after."}
                  </Text>
                </View>
              )
              : inventory.data
              ? (
                <>
                  <Text className="text-xs font-label tracking-wide text-muted-foreground">
                    {inventory.data.cards.length}{" "}
                    {inventory.data.cards.length === 1 ? "card" : "cards"}
                  </Text>
                  {inventory.data.cards.map((card) => (
                    <CardResultRow
                      key={card.card_id}
                      card={card}
                      onPress={() => {
                        const params = new URLSearchParams({ q: settledQuery.trim() });
                        router.push(
                          `/shows/${encodeURIComponent(show.public_id)}/cards/${
                            encodeURIComponent(card.card_id)
                          }?${params}`,
                        );
                      }}
                    />
                  ))}
                </>
              )
              : null}
          </View>
        )
        : null}

      {show.state === "canceled"
        ? (
          <View
            testID="show-canceled-notice"
            accessibilityRole="alert"
            className="mt-4 rounded-xl bg-warning-soft p-4"
          >
            <Text className="font-title text-foreground">This Show is canceled</Text>
            {show.cancellation_reason
              ? <Text className="mt-1 font-body text-foreground">{show.cancellation_reason}</Text>
              : null}
          </View>
        )
        : null}

      {show.state !== "canceled"
        ? (
          <View className="mt-10 flex-row flex-wrap items-end justify-between gap-3">
            <View>
              <Text
                accessibilityRole="header"
                aria-level={2}
                className="text-2xl font-title text-foreground"
              >
                Find your table
              </Text>
              <Text className="mt-1 font-body text-sm text-muted-foreground">
                Select a Vendor to highlight their location on the floor plan.
              </Text>
            </View>
            {plan && Platform.OS === "web"
              ? <Button title="Print plan" variant="outline" onPress={print} />
              : null}
          </View>
        )
        : null}

      <View
        className={show.state !== "canceled" && isDesktop && plan && !mapCollapsed
          ? "mt-3 flex-row items-start gap-4"
          : "mt-3 gap-4"}
      >
        {show.state === "canceled"
          ? null
          : !plan || show.plan_state === "unavailable"
          ? (
            <View
              testID={show.plan && !plan ? "show-plan-error" : "show-plan-unavailable"}
              className="flex-1 rounded-xl border border-border bg-surface p-5"
            >
              <Text className="font-title text-foreground">
                {show.plan && !plan
                  ? "The floor plan could not be displayed."
                  : "The floor plan is not available yet."}
              </Text>
              <Text className="mt-1 font-body text-muted-foreground">
                {show.plan && !plan
                  ? "The Vendor roster is still available."
                  : "Check back after the Organizer publishes it."}
              </Text>
            </View>
          )
          : mapCollapsed
          ? (
            <View testID="show-map-collapsed" className="rounded-xl bg-surface-muted p-4">
              <Text className="font-title text-foreground">Floor plan shown as a list</Text>
              <Text className="mt-1 font-body text-muted-foreground">
                At this text size, Vendor locations are clearer in the roster.
              </Text>
            </View>
          )
          : (
            <View
              testID="show-floor-plan"
              className="min-h-[420px] flex-1 overflow-hidden rounded-xl border border-border bg-surface"
            >
              <FloorPlanCanvas
                document={plan.document}
                tableStates={tableStates}
                highlightedTableIds={selected?.table_ids ?? []}
                onTablePress={(tableId) => {
                  const assignment = assignments.find((item) => item.tableId === tableId);
                  setSelectedParticipationId(assignment?.participationId ?? null);
                }}
              />
            </View>
          )}

        <View
          testID="show-roster"
          className={show.state !== "canceled" && isDesktop && plan && !mapCollapsed
            ? "w-80"
            : "w-full"}
        >
          <Text
            accessibilityRole="header"
            aria-level={show.state === "canceled" ? 2 : 3}
            className="text-lg font-title text-foreground"
          >
            Vendor roster
          </Text>
          {show.roster.length > 0
            ? (
              <Input
                className="mt-2"
                value={rosterQuery}
                onChangeText={setRosterQuery}
                placeholder="Find a Vendor"
                accessibilityLabel="Find a Vendor"
                maxLength={120}
              />
            )
            : null}
          {show.plan_state === "empty"
            ? (
              <Text testID="show-inventory-empty" className="mt-3 font-body text-muted-foreground">
                The hall is published, but no tables are assigned yet.
              </Text>
            )
            : null}
          {show.roster.length === 0
            ? (
              <Text testID="show-roster-empty" className="mt-3 font-body text-muted-foreground">
                No Vendors are listed yet.
              </Text>
            )
            : roster.length === 0
            ? (
              <Text
                testID="show-roster-no-matches"
                className="mt-3 font-body text-muted-foreground"
              >
                No Vendors match “{rosterQuery.trim()}”.
              </Text>
            )
            : null}
          <View testID={mapCollapsed ? "show-map-list" : undefined} className="mt-3 gap-2">
            {roster.map((vendor) => {
              const active = vendor.participation_id === selectedParticipationId;
              const numbers = vendor.table_ids.map((tableId) =>
                tableNumbers.get(tableId) ?? tableId
              );
              const tables = numbers.length
                ? `at table ${numbers.join(", ")}`
                : "No table assigned";
              const location = plan ? describeShowLocation(plan.document, vendor.table_ids) : null;
              return (
                <View key={vendor.participation_id}>
                  <Pressable
                    testID="show-vendor-row"
                    accessibilityRole="button"
                    aria-pressed={active}
                    accessibilityLabel={`${vendor.name}, ${tables}${
                      location ? `, ${location}` : ""
                    }`}
                    onPress={() =>
                      setSelectedParticipationId(active ? null : vendor.participation_id)}
                    className={active
                      ? "min-h-12 rounded-lg border-2 border-primary bg-accent-soft p-3"
                      : "min-h-12 rounded-lg border border-border bg-surface p-3 focus:border-primary active:bg-surface-muted"}
                  >
                    <View className="flex-row flex-wrap items-center gap-2">
                      <Text className="font-title text-foreground">{vendor.name}</Text>
                      {vendor.buylist_slug
                        ? <Badge label="✦ Buying" />
                        : null}
                    </View>
                    <Text className="mt-0.5 text-xs font-body text-muted-foreground">
                      {tables}
                      {location ? ` · ${location}` : ""}
                      {!vendor.searchable ? " · No public inventory yet" : ""}
                    </Text>
                  </Pressable>
                  {active
                    ? (
                      <View
                        testID="show-selected-vendor"
                        accessibilityLiveRegion="polite"
                        className="gap-3 px-3 pb-2 pt-3"
                      >
                        <Text className="font-body text-sm text-muted-foreground">
                          {vendor.searchable
                            ? `${vendor.name} has public inventory for this Show.`
                            : `${vendor.name} has not published inventory for this Show.`}
                        </Text>
                        {vendor.searchable
                          ? (
                            <View className="self-start">
                              <Button
                                title="View Vendor inventory"
                                size="sm"
                                variant="outline"
                                accessibilityRole="link"
                                onPress={() => {
                                  const suffix = query
                                    ? `?${new URLSearchParams({ q: query })}`
                                    : "";
                                  router.push(
                                    `/shows/${encodeURIComponent(show.public_id)}/vendors/${
                                      encodeURIComponent(vendor.participation_id)
                                    }${suffix}`,
                                  );
                                }}
                              />
                            </View>
                          )
                          : null}
                        {vendor.buylist_slug
                          ? (
                            <View className="self-start">
                              <Button
                                title="See what they're buying"
                                size="sm"
                                variant="outline"
                                accessibilityRole="link"
                                onPress={() =>
                                  void Linking.openURL(
                                    `${APP_BASE_URL}${buylistPath(vendor.buylist_slug!)}?show=${
                                      encodeURIComponent(show.public_id)
                                    }`,
                                  )}
                              />
                            </View>
                          )
                          : null}
                      </View>
                    )
                    : null}
                </View>
              );
            })}
          </View>
        </View>
      </View>

      {vendorTables}

      {show.description || show.organizer?.other_listed_shows?.length
        ? (
          <View
            className={isDesktop
              ? "mt-10 flex-row gap-12 border-t border-border py-10"
              : "mt-10 gap-8 border-t border-border py-8"}
          >
            {show.description
              ? (
                <View className="min-w-0 flex-1 gap-2">
                  <Text
                    accessibilityRole="header"
                    aria-level={2}
                    className="text-2xl font-title text-foreground"
                  >
                    About this Show
                  </Text>
                  <Text className="font-body text-base leading-7 text-foreground">
                    {show.description}
                  </Text>
                </View>
              )
              : null}
            {show.organizer?.other_listed_shows?.length
              ? (
                <View className={isDesktop ? "w-80 gap-2" : "gap-2"}>
                  <Text className="text-xs font-label uppercase tracking-wide text-muted-foreground">
                    More from {show.organizer.display_name}
                  </Text>
                  {show.organizer.other_listed_shows.map((other) => (
                    <Pressable
                      key={other.public_id}
                      accessibilityRole="link"
                      accessibilityLabel={`Open ${other.name}`}
                      onPress={() => router.push(`/shows/${encodeURIComponent(other.public_id)}`)}
                      className="min-h-12 flex-row items-center justify-between border-b border-border py-3 active:bg-surface-muted"
                    >
                      <Text className="font-title text-foreground">{other.name}</Text>
                      <ChevronRight aria-hidden size={18} color={colors.subtle} />
                    </Pressable>
                  ))}
                </View>
              )
              : null}
          </View>
        )
        : null}

      {calendarUrls
        ? (
          <Sheet
            visible={calendarOpen}
            onClose={() => setCalendarOpen(false)}
            title="Add to calendar"
            subtitle="Choose a calendar. The calendar file keeps each Show session separate."
            closeButton
            className="gap-3"
          >
            <Button
              title="Google Calendar"
              variant="outline"
              accessibilityRole="link"
              onPress={() =>
                void openAction(calendarUrls.google, "Google Calendar could not be opened.", true)}
            />
            <Button
              title="Outlook"
              variant="outline"
              accessibilityRole="link"
              onPress={() =>
                void openAction(calendarUrls.outlook, "Outlook could not be opened.", true)}
            />
            <Button
              title="Calendar file (.ics)"
              variant="outline"
              accessibilityRole="link"
              onPress={() =>
                void openAction(calendarUrls.ics, "The calendar file could not be opened.", true)}
            />
            {actionError
              ? (
                <Text accessibilityRole="alert" className="font-body text-sm text-loss">
                  {actionError}
                </Text>
              )
              : null}
          </Sheet>
        )
        : null}
    </View>
  );
}
